<template>
  <div class="pt-2">
    <!-- Floating Action Bar for bulk actions -->
    <FloatingActionBar
      :selected="selected"
      :totalItems="totalItems"
      :showDeleteSelected="hasIamPermission(iamStatements, `${permissionPrefix}.delete`)"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="handleShowDialogDelete"
    />
    <div v-if="!isLoad">
      <template v-if="!$vuetify.breakpoint.smAndDown">
        <div class="d-flex flex-column flex-md-row position-relative">
          <!-- Left Sidebar for Filters -->
          <div
            class="ecom-sidebar-header border-r mb-2 mb-md-0 position-relative"
            :style="{
              flex: sidebarCollapsed ? '0 0 48px' : '0 0 260px',
              maxWidth: sidebarCollapsed ? '48px' : '260px',
              transition: 'width 0.22s ease, flex 0.22s ease, max-width 0.22s ease',
              overflow: 'hidden'
            }"
          >
            <!-- Collapsed view -->
            <div
              v-if="sidebarCollapsed"
              class="d-flex flex-column align-center justify-start pt-3 cursor-pointer fill-height"
              @click="sidebarCollapsed = false"
              style="height: 100%"
            >
              <v-icon size="22" color="primary" class="mb-2">mdi-filter-outline</v-icon>
              <div
                style="
                  writing-mode: vertical-rl;
                  text-orientation: mixed;
                  transform: rotate(180deg);
                  font-size: 11px;
                  font-weight: 700;
                  color: #475569;
                  letter-spacing: 0.05em;
                  text-transform: none;
                  white-space: nowrap;
                "
              >
                Filters
              </div>
            </div>

            <!-- Expanded view -->
            <template v-else>
              <div class="d-flex align-center justify-space-between px-4 pb-2 pt-1 border-b">
                <span
                  class="text-subheading-sm font-semibold black--text normal-case"
                  >Filters</span
                >
                <button
                  class="text-caption font-semibold normal-case"
                  :class="
                    hasActiveFilters
                      ? 'cursor-pointer primary--text'
                      : 'cursor-default text-slate-400'
                  "
                  style="background: none; border: none; padding: 0"
                  :disabled="!hasActiveFilters"
                  @click="clearAllFilters"
                >
                  Clear All
                </button>
              </div>

              <div class="pt-2 ecom-sidebar">
                <EcomFilterCategory title="Customer" v-model="filterCustomer" @change="handleFilterChange">
                  <div class="px-1 py-1">
                    <v-text-field
                      v-model="filterCustomer"
                      placeholder="Search Customer..."
                      dense
                      outlined
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      @input="handleFilterChange"
                    ></v-text-field>
                  </div>
                </EcomFilterCategory>

                <EcomFilterCategory
                  title="Customer Type"
                  v-model="filterCustomerType"
                  :items="customerTypeOptions"
                  @change="handleFilterChange"
                />

                <EcomFilterCategory
                  v-if="isEntityEnabled && entities && entities.length > 0"
                  :title="entityTitle || 'Entity'"
                  v-model="entityId"
                  :items="entities"
                  itemText="entityName"
                  itemValue="_id"
                  :searchable="true"
                  @change="handleFilterChange"
                />
              </div>
            </template>
          </div>

          <!-- Sidebar Toggle Button -->
          <div
            style="
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              z-index: 10;
              pointer-events: none;
              display: flex;
              align-items: center;
            "
            :style="{ left: sidebarCollapsed ? '48px' : '260px', transition: 'left 0.22s ease' }"
          >
            <button
              @click="sidebarCollapsed = !sidebarCollapsed"
              style="
                pointer-events: all;
                width: 18px;
                height: 48px;
                background: #cbd5e1;
                border: 1px solid #94a3b8;
                border-left: none;
                border-radius: 0 6px 6px 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                box-shadow: 2px 0 6px rgba(0, 0, 0, 0.12);
                padding: 0;
                outline: none;
                transition: background 0.15s;
              "
              @mouseover="$event.currentTarget.style.background = '#94a3b8'"
              @mouseleave="$event.currentTarget.style.background = '#cbd5e1'"
            >
              <v-icon size="13" color="grey darken-3">
                {{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}
              </v-icon>
            </button>
          </div>

          <!-- Main Table Content -->
          <div class="flex-grow-1 overflow-hidden pl-md-2" style="min-width: 0">
            <div class="flex justify-end items-center mb-2" v-if="!caseConfig?._id">
              <div class="justify-end mr-4" :class="[$vuetify.breakpoint.lgAndUp ? 'flex' : 'flex-col']">
                <v-btn
                  @click="addNewCustomer"
                  v-iam-disabled="`${permissionPrefix}.write`"
                  :color="$vuetify.theme.currentTheme.primary"
                  class="normal-case ml-4 white--text my-auto"
                >
                  <v-icon left dark>mdi-plus</v-icon>
                  Add
                </v-btn>
                <v-btn
                  @click="exportToExcel()"
                  v-iam-disabled="`${permissionPrefix}.export`"
                  :color="$vuetify.theme.currentTheme.primary"
                  class="normal-case ml-4 white--text my-auto"
                >
                  <v-icon left dark>mdi-download</v-icon>
                  Export
                </v-btn>
              </div>
            </div>

            <div class="overflow-y-auto max-h-[calc(100vh-150px)]">
              <v-data-table
                :headers="headers"
                :items="customers"
                class="table-radius border border-gray-200"
                fixed-header
                :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30, 50] }"
                :options.sync="options"
                :server-items-length="totalItems"
                :loading="loadTable"
                v-model="selected"
                show-select
                item-key="_id"
                disable-sort
              >
                <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
                  <p
                    :key="`tooltip-${index}`"
                    class="font-title"
                    v-if="header.text?.length > 15"
                    v-tooltip="{ text: header.text }"
                  >
                    {{ header.text }}
                  </p>
                  <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
                </template>
                <template v-slot:item.customerName="{ item }">
                  <td class="px-4 py-3 cursor-pointer" @click="showCustomerDetails(item)">
                    <span class="text-blue-600 font-bold hover:underline">
                      {{ userFullName(item) }}
                    </span>
                  </td>
                </template>
                <template v-slot:item.customerType="{ item }">
                  <td class="px-4 py-3 capitalize">
                    <v-chip
                      x-small
                      :color="item.customerType === 'company' ? 'purple lighten-5 purple--text text--darken-3' : 'blue lighten-5 blue--text text--darken-3'"
                      class="font-weight-medium capitalize"
                    >
                      {{ item.customerType ? (item.customerType === 'company' ? 'Company' : 'Individual') : 'Individual' }}
                    </v-chip>
                  </td>
                </template>
                <template v-slot:item.entityName="{ item }">
                  <td class="px-4 py-3">
                    {{ item.entity?.entityName || '-' }}
                  </td>
                </template>
                <template v-slot:item.email="{ item }">
                  <td class="px-4 py-3">{{ item.customerEmail }}</td>
                </template>
                <template v-slot:item.customerMobile="{ item }">
                  <td class="px-4 py-3">
                    {{ maskNumber(item.customerMobile[0]) }}
                  </td>
                </template>
                <template v-slot:item.address="{ item }">
                  <td class="px-4 py-3" v-if="item.customerAddress.length">
                    {{ customerAddress(item.customerAddress[0]) || '-' }}
                    <br />
                    {{
                      item.customerAddress[0]?.typeOfAddress
                        ? `(${item.customerAddress[0]?.typeOfAddress})`
                        : ''
                    }}
                  </td>
                  <td v-else>-</td>
                </template>
                <template v-slot:item.action="{ item }">
                  <td class="px-4 py-3">
                    <action-button
                      :item="item"
                      :canEdit="hasIamPermission(iamStatements, `${permissionPrefix}.write`)"
                      :showEditButton="hasIamPermission(iamStatements, `${permissionPrefix}.write`)"
                      :handleEdit="() => customerEdit(item)"
                      :handleDelete="() => customerDelete(item)"
                      :showDeleteButton="hasIamPermission(iamStatements, `${permissionPrefix}.delete`)"
                      :canDelete="hasIamPermission(iamStatements, `${permissionPrefix}.delete`)"
                    />
                  </td>
                </template>
              </v-data-table>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <CollectionCustomer_mobile />
      </template>

      <v-dialog v-model="openAddCustomer" :fullscreen="$vuetify.breakpoint.smAndDown" :width="$vuetify.breakpoint.smAndDown ? '100%' : '63rem'" persistent>
        <AddCustomer_mobile
          v-if="$vuetify.breakpoint.smAndDown"
          @customerAdded="handleNewCustomer"
          :editCustomer="editCustomer"
          :caseType="relatedCaseType"
          :entities="entities"
          :configTabMapping="configTabMapping"
          @emittedEntity="handleEntity"
        />
        <AddCustomer
          v-else
          @customerAdded="handleNewCustomer"
          :editCustomer="editCustomer"
          :caseType="relatedCaseType"
          :entities="entities"
          :configTabMapping="configTabMapping"
          @emittedEntity="handleEntity"
        />
      </v-dialog>
      <v-dialog v-model="showCustomerDetailsPopup" :fullscreen="$vuetify.breakpoint.smAndDown" :max-width="$vuetify.breakpoint.smAndDown ? '100%' : '1200'" content-class="rounded-md">
        <CustomerDetails_mobile
          v-if="$vuetify.breakpoint.smAndDown"
          :customer="selectedCustomer"
          :closeCustomerPopUp="closeCustomerPopUpFunc"
        />
        <CustomerDetails
          v-else
          :customer="selectedCustomer"
          :closeCustomerPopUp="closeCustomerPopUpFunc"
        />
      </v-dialog>
      <v-dialog v-model="whatsAppMessagePopupVisible" max-width="500">
        <WhatsAppMessage :customers="selected" />
      </v-dialog>
      <AlertPopUp
        :showConfirmationDialog="showConfirmationDialog"
        :confirmationTitle="'Confirm Delete'"
        :confirmationMessage="`Are you sure you want to delete the ${
          configTabMapping.customerTable?.displayName || 'Customer'
        }?`"
        :confirmationActionText="'DELETE'"
        :confirmationActionColor="'red'"
        :performAction="custDeleteAction"
        :cancel="cancelDelete"
      />
      <AlertPopUp
        :showConfirmationDialog="showDeleteDialog"
        :confirmationTitle="'Confirm Delete'"
        :confirmationMessage="`Do you want to delete selected ${
          configTabMapping.customerTable?.displayName || 'Customer'
        }?`"
        :confirmationActionText="'DELETE'"
        :confirmationActionColor="'red'"
        :performAction="customerDeleteFunc"
        :cancel="handleShowDialogDelete"
        :loading="loading"
      />
    </div>
    <div v-else class="absolute w-full h-screen flex-col items-center flex justify-center">
      <v-progress-circular
        :color="$vuetify.theme.currentTheme.primary"
        indeterminate
        :size="50"
        :width="5"
      ></v-progress-circular>
      <div class="mt-2"><span>Please Wait...</span></div>
    </div>
    <v-dialog v-model="openUploadCustomers" max-width="1000" persistent>
      <GenericBulkUpload
        :title="'Bulk Customer Upload'"
        :subtitle="'Upload multiple customers using Excel file'"
        :entityName="'Customer'"
        :uploadEndpoint="'/customer/bulk'"
        :requiredColumns="['crn', 'First Name', 'Last Name', 'Customer Full Name']"
        :sampleData="[
          {
            crn: '123456',
            'First Name': 'John',
            'Last Name': 'Doe',
            'Middle Name': 'M',
            'Client Name': 'ABC Corp',
            'Date of birth': '1990-01-01',
            Gender: 'MALE',
            'Customer Email': 'john@example.com',
            'Customer Number 1': '9999999999',
            'Customer Number 2': '8888888888',
            Occupation: 'Engineer',
            Company: 'Tech Corp',
            Jobtype: 'Full Time',
            Salary: '50000',
            'Father Name': 'James Doe',
            'Mother Name': 'Jane Doe',
            Entity: 'Company XYZ'
          }
        ]"
        :successKey="'customerCreatedSuccessfully'"
        :failedKey="'customerCreationFailed'"
        :invalidItemsKey="'invalidCustomers'"
        :additionalPayload="{ caseType: caseConfig?.name, branch: caseConfig?.branch }"
        @uploadComplete="handleBulkUploadComplete"
        @close="closeUpload"
      />
    </v-dialog>
  </div>
</template>
<script>
import AddCustomer from '@/components/Workforce/CollectionModules/AddCollCustomer'
import AddCustomer_mobile from '@/components/Workforce/CollectionModules/AddCollCustomer_mobile'
import CustomerDetails from '@/components/Workforce/CollectionModules/CustomerDetails'
import CustomerDetails_mobile from '@/components/Workforce/CollectionModules/CustomerDetails_mobile'
import CollectionCustomer_mobile from './CollectionCustomer_mobile.vue'
import AlertPopUp from '@/components/AlertPopUp'
import GenericBulkUpload from '@/components/Workforce/CollectionManagement/GenericBulkUpload.vue'
import { fullName, getFullAddress, debounce, maskPhoneNumber } from '@/utils/common'
import ActionButton from '@/components/ActionButton.vue'
import WhatsAppMessage from '@/components/Workforce/WhatsAppTemplateMessage.vue'
import * as XLSX from 'xlsx'
import FloatingActionBar from '@/components/Workforce/CollectionManagement/CaseModules/FloatingActionBar.vue'
import { axiosUrl } from '@/utils/workforceApi'
import { hasIamPermission } from '@/utils/iamEvaluator'
import EcomFilterCategory from '@/components/EcomFilterCategory.vue'
export default {
  name: 'CollectionCustomer',
  provide() {
    return {
      parentPage: this
    }
  },
  inject: {
    getDynamicPermissions: {
      default: () => ({ useCase: '', subCase: '' })
    }
  },
  layout: 'Workforce/default',
  components: {
    ActionButton,
    AddCustomer,
    AddCustomer_mobile,
    CustomerDetails,
    CustomerDetails_mobile,
    CollectionCustomer_mobile,
    AlertPopUp,
    WhatsAppMessage,
    GenericBulkUpload,
    FloatingActionBar,
    EcomFilterCategory
  },
  props: {
    caseConfig: Object
  },
  data() {
    return {
      sidebarCollapsed: false,
      openAddCustomer: false,
      whatsAppMessagePopupVisible: false,
      loadTable: false,
      showCustomerDetailsPopup: false,
      customers: [],
      editCustomer: null,
      options: { itemsPerPage: 10, page: 1 },
      selectedCustomer: null,
      deleteCustomer: null,
      totalItems: 0,
      filterCustomer: '',
      filterCustomerType: null,
      customerTypeOptions: [
        { label: 'Individual', value: 'individual' },
        { label: 'Company', value: 'company' }
      ],
      showConfirmationDialog: false,
      openUploadCustomers: false,
      selected: [],
      showDeleteDialog: false,
      loading: false,
      isLoad: true,
      entityId: null,
      searchValue: '',
      entities: [],
      isAnyEntityEnabled: false,
      selectedCount: 0,
      entityDisplayName: ''
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchCustomers()
      },
      deep: true
    }
  },
  computed: {
    hasActiveFilters() {
      const hasCust = !!(this.filterCustomer && this.filterCustomer.trim())
      const hasType = !!this.filterCustomerType
      const hasEntity = !!this.entityId
      const hasSearch = !!(this.searchValue && this.searchValue.trim())
      return !!(hasCust || hasType || hasEntity || hasSearch)
    },
    relatedCaseType() {
      return this.$props.caseConfig?.name || ''
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    permissionPrefix() {
      const getDyn = this.getDynamicPermissions
      const { useCase, subCase } = typeof getDyn === 'function' ? getDyn() : {}
      if (useCase && subCase) {
        return useCase + '.' + subCase + '.customers'
      }
      return 'customers.details'
    },
    headers() {
      const headers = [
        {
          text: `${this.configTabMapping.customerTable?.displayName || 'Customer'} Name`,
          value: 'customerName',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        },
        {
          text: 'Customer Type',
          value: 'customerType',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        },
        {
          text: 'Email',
          value: 'email',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        },
        {
          text: 'Phone No',
          value: 'customerMobile',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        },
        {
          text: 'Address',
          value: 'address',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        },
        {
          text: 'Action',
          value: 'action',
          sortable: false,
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        }
      ]
      if (this.isEntityEnabled) {
        headers.splice(2, 0, {
          text: this.entityTitle,
          value: 'entityName',
          sortable: false,
          width: '150px',
          headerClass: 'font-medium whitespace-nowrap overflow-hidden text-ellipsis'
        })
      }

      return headers
    },
    isEntityEnabled() {
      return this.caseConfig ? this.caseConfig.entity?.enabled : this.isAnyEntityEnabled
    },
    entityTitle() {
      return this.caseConfig ? this.caseConfig.entity?.displayName : this.entityDisplayName
    },
    configTabMapping() {
      return this.caseConfig?.tableTabMapping || {}
    },
    customerDyanmicParams() {
      let params = {}
      if (this.$route.name === 'DynamicUseCase') {
        params.useCase = this.caseConfig?.useCase
        params.subCase = this.caseConfig?._id
      } else {
        params.relatedCategory = this.relatedCaseType ? 'collection' : undefined
        params.relatedSubCategories = this.relatedCaseType || undefined
      }
      return params
    }
  },
  methods: {
    hasIamPermission,
    bulkCustomer() {
      this.openUploadCustomers = true
    },
    closeUpload() {
      this.openUploadCustomers = false
      this.fetchCustomers() // Refresh the customers list after upload
    },
    handleBulkUploadComplete() {
      this.closeUpload()
      this.$toast.success('Customers bulk upload completed successfully!')
    },
    maskNumber(num) {
      return maskPhoneNumber(num)
    },
    closeCustomerPopUpFunc() {
      this.showCustomerDetailsPopup = false
    },
    async fetchCustomers() {
      try {
        this.loadTable = true
        const { page, itemsPerPage } = this.options
        const customerUseCase = this.customerDyanmicParams
        const params = {
          page,
          limit: itemsPerPage > -1 ? itemsPerPage : this.totalItems,
          ...customerUseCase
        }
        if (this.entityId) {
          params.entity = this.entityId
        }
        if (this.filterCustomer) {
          params.search = this.filterCustomer
        }
        if (this.filterCustomerType) {
          params.customerType = this.filterCustomerType
        }

        const response = await this.$axios.get(axiosUrl.customers.getCustomers, {
          params
        })
        this.customers = response.data.customers
        this.loadTable = false
        this.totalItems = response.data?.pagination?.totalCount
      } catch (error) {
        console.log(error)
        const errorMessage = error.response?.data?.message || error
        this.$toast.error(errorMessage)
        this.loadTable = false
      }
    },
    customerDelete(item) {
      this.deleteCustomer = item
      this.showConfirmationDialog = true
    },
    userFullName(item) {
      if (item.customerFullName) {
        return item.customerFullName
      } else if (item.firstName) {
        return fullName(item)
      } else {
        return ''
      }
    },
    async custDeleteAction() {
      try {
        const item = this.deleteCustomer
        const response = await this.$axios.delete(
          `${axiosUrl.customers.deleteCustomers}/${item._id}`
        )
        const index = this.customers.findIndex((cust) => cust._id === item._id)
        if (index !== -1) {
          this.customers.splice(index, 1)
        }
        const successMessage = response?.data?.message || 'Customer deleted successfully.'
        this.$toast.success(successMessage)
        this.cancelDelete()
      } catch (error) {
        const errorMessage =
          error.response.data.message || 'An error occurred while deleting the customer.'
        this.$toast.error(errorMessage)
        console.error(error)
      }
    },
    async singleCustomerDelete(item) {
      try {
        let response = await this.$axios.delete(`${axiosUrl.customers.deleteCustomers}/${item._id}`)
        return response.data
      } catch (error) {
        console.error(error)
      }
    },
    async customerDeleteFunc() {
      this.loading = true

      const promises = []

      // Make API calls and store promises in the array
      this.selected?.forEach(async (item) => {
        promises.push(this.singleCustomerDelete(item))
      })

      // Wait for all API calls to complete
      try {
        const results = await Promise.all(promises)

        if (!results.includes(undefined)) {
          this.$toast.success('Customers deleted successfully.')
        }
        this.loading = false
        this.selected = []
        this.handleShowDialogDelete(false)
      } catch (error) {
        this.$toast.error('An error occurred while deleting the case.')
        console.error(error)
      }
    },
    getAllEntities: debounce(async function () {
      try {
        const params = {}
        if (this.searchValue) {
          params.search = this.searchValue
        }
        const response = await this.$axios.get('/collection/entities', { params })
        this.entities = response?.data?.entities
      } catch (error) {
        console.log(error)
      }
    }, 500),
    async getConfig() {
      try {
        const res = await this.$axios.get(`/orgSetup/extend-usecase`)
        const allSubCases = res.data?.data
        const enabledEntity = allSubCases.find((el) => el.entity?.enabled)
        this.isAnyEntityEnabled = enabledEntity?.entity?.enabled || false
        if (this.isAnyEntityEnabled) {
          this.entityDisplayName = enabledEntity?.entity?.displayName || 'Entity'
        }
      } catch (error) {
        console.error('Error fetching config:', error)
      }
    },
    async handleEntity(val) {
      this.searchValue = val
      await this.getAllEntities()
    },
    cancelDelete() {
      this.showConfirmationDialog = false
      this.deleteCustomer = null
      this.fetchCustomers()
    },
    customerEdit(item) {
      this.editCustomer = item
      this.openAddCustomer = true
    },
    customerAddress(item) {
      if (item) {
        return getFullAddress(item)
      }
      return '-'
    },
    updateSelectedCount() {
      this.selectedCount = [this.entityId].filter(
        (item) => item !== null && item !== '' && item.length > 0
      ).length
    },
    truncateName(name) {
      const maxLength = 15
      if (name.length > maxLength) {
        return name.slice(0, maxLength) + '...'
      }
      return name
    },
    addNewCustomer() {
      this.editCustomer = null
      this.openAddCustomer = true
    },
    handleNewCustomer() {
      this.openAddCustomer = false
      this.fetchCustomers()
    },
    showCustomerDetails(customer) {
      this.selectedCustomer = customer
      this.showCustomerDetailsPopup = true
    },
    handleInput: debounce(async function () {
      this.options.page = 1
      this.updateSelectedCount()
      await this.fetchCustomers()
    }, 800),

    handleShowDialogDelete() {
      this.showDeleteDialog = !this.showDeleteDialog
      if (this.showDeleteDialog === false) {
        this.fetchCustomers()
      }
    },
    async exportToExcel() {
      try {
        this.loadTable = true
        const params = {
          relatedCategory: this.relatedCaseType ? 'collection' : undefined,
          relatedSubCategories: this.relatedCaseType || undefined,
          entity: this.entityId,
          search: this.filterCustomer
        }

        // Get total count
        const countResponse = await this.$axios.get(axiosUrl.customers.exportToExcel, {
          params: { ...params, page: 1, limit: 1 }
        })
        const totalRecords = countResponse.data?.pagination?.totalCount || 0

        if (totalRecords === 0) {
          this.$toast.info('No records found to export')
          return
        }

        // Fetch all customers in batches
        const batchSize = 50
        const totalBatches = Math.ceil(totalRecords / batchSize)
        const batchPromises = Array.from({ length: totalBatches }, (_, i) =>
          this.$axios
            .get(axiosUrl.customers.exportToExcel, {
              params: { ...params, page: i + 1, limit: batchSize }
            })
            .then((response) => response.data.customers || [])
            .catch((error) => {
              console.error(`Batch ${i + 1} failed:`, error)
              return []
            })
        )

        const results = await Promise.all(batchPromises)
        const allCustomers = results.flat()

        if (!allCustomers.length) return

        // Prepare export data
        const exportData = allCustomers.map((customer, index) => {
          const baseData = {
            'S.No': index + 1,
            'Customer Name': this.userFullName(customer),
            CRN: customer.crn || '-',
            Email: customer.customerEmail || '-',
            'Phone No': customer.customerMobile?.[0] || '-',
            Address: customer.customerAddress?.[0]
              ? this.customerAddress(customer.customerAddress[0])
              : '-',
            'Address Type': customer.customerAddress?.[0]?.typeOfAddress || '-',
            'Created By':
              `${
                customer.createdBy?.systemUserId?.firstName || customer.createdBy?.firstName || ''
              } ${
                customer.createdBy?.systemUserId?.lastName || customer.createdBy?.lastName || ''
              }`.trim() || 'System',
            'Created At': this.$dayjs(customer.createdAt).format('DD/MM/YYYY hh:mm A') || '-',
            'Updated By':
              `${
                customer.updatedBy?.systemUserId?.firstName || customer.updatedBy?.firstName || ''
              } ${
                customer.updatedBy?.systemUserId?.lastName || customer.updatedBy?.lastName || ''
              }`.trim() || 'System',
            'Updated At': this.$dayjs(customer.updatedAt).format('DD/MM/YYYY hh:mm A') || '-'
          }

          // Only add entity column if enabled
          if (this.isEntityEnabled) {
            baseData[this.entityTitle || 'Entity'] = customer.entity?.entityName || '-'
          }

          return baseData
        })

        // Create Excel workbook
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(exportData)

        // Set column widths
        const headers = Object.keys(exportData[0] || {})
        worksheet['!cols'] = headers.map((header) => ({
          wch: Math.min(
            Math.max(header.length, ...exportData.map((row) => String(row[header] || '').length)) +
              2,
            50
          )
        }))

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers')

        // Generate filename and save
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
        const filename = `Customers_Export_${timestamp}.xlsx`
        XLSX.writeFile(workbook, filename)

        this.$toast.success(
          `Excel file exported successfully with ${allCustomers.length} records: ${filename}`
        )
      } catch (error) {
        console.error('Export error:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to export data to Excel')
      } finally {
        this.loadTable = false
      }
    },
    clearAllFilters() {
      this.filterCustomer = ''
      this.filterCustomerType = null
      this.entityId = null
      this.searchValue = ''
      this.options.page = 1
      this.fetchCustomers()
    },
    handleFilterChange: debounce(async function () {
      this.options.page = 1
      await this.fetchCustomers()
    }, 500)
  },
  async mounted() {
    await this.getConfig()
    await this.getAllEntities()
    this.isLoad = false
  },
  beforeCreate() {
    this.debouncedFetchCustomers = () => {}
  },
  created() {
    this.debouncedFetchCustomers = debounce(this.fetchCustomers, 800)
  }
}
</script>
<style scoped>
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: var(--font-subheading-sm) !important;
  font-weight: var(--font-weight-semibold) !important;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ecom-sidebar-header {
  border-right: 1px solid #e9e9ed;
  flex: 0 0 260px;
  max-width: 260px;
}
.ecom-sidebar {
  height: calc(100vh - 200px);
  overflow-y: auto;
}
.border-b {
  border-bottom: 1px solid #e9e9ed;
}

@media (max-width: 959px) {
  .ecom-sidebar {
    flex: 0 0 100%;
    max-width: 100%;
    height: 35vh;
    border-right: none !important;
    border-bottom: 1px solid #e9e9ed;
  }
}
</style>
